import React, { useEffect } from "react";
import { validateProps } from "../../../../shared/props";
import { Stack } from "../../../Stacks/Stack/Stack";
import { ListItemCanvas, ListItemCanvasExternalProps } from "../shared/ListItemCanvas";
import { validColumnComponents } from "../shared/MultiColumnListItem.types";
import { gapBetweenElements, listItemPadding } from "../shared/styles";
import { validateListItemColumnComponent } from "../shared/utils";

export interface MobileListItemProps extends ListItemCanvasExternalProps {
    /**
     * Column component to fill the left side of MobileListItem.
     * Note: it is recommended to utilize TitleColumns for the best mobile experience.
     */
    readonly leftColumn: JSX.Element;

    /**
     * Column component to fill the right side of the MobileListItem.
     * Note: it is recommended to utilize the FinalColumns for the best mobile experience.
     */
    readonly rightColumn: JSX.Element;

    /**
     * Optional flag to indicate that the list item's contents are disabled.
     */
    readonly disabled?: boolean;
}

// tslint:disable: no-magic-numbers
export const MobileListItem: React.FC<MobileListItemProps> = ({
    leftColumn,
    rightColumn,
    disabled = false,
    menuDisabled = false,
    cellRole,
    customListItemPadding,
    ...otherProps
}) => {
    validateProps("MobileListItem", otherProps);

    //Ensures we only run the validation once at first, and then only for each
    //Column that changes after the first render
    useEffect(() => {
        validateListItemColumnComponent(leftColumn, validColumnComponents);
    }, [leftColumn]);

    useEffect(() => {
        validateListItemColumnComponent(rightColumn, validColumnComponents);
    }, [rightColumn]);

    // Get column and assign role if present
    const getColumn = (column: JSX.Element) => {
        return cellRole ?
            (
                <div role={cellRole}>
                    {
                        React.cloneElement(column, { disabled })
                    }
                </div>
            ) :
            React.cloneElement(column, { disabled });
    };

    const baseContent = (
        <Stack
            verticalAlign="center"
            horizontalAlign="space-between"
            horizontal={true}
            childrenGap={gapBetweenElements}
        >
            <Stack verticalAlign="center" grow={true}>
                {getColumn(leftColumn)}
            </Stack>
            <Stack verticalAlign="center" grow={true}>
                {getColumn(rightColumn)}
            </Stack>
        </Stack>
    );

    return (
        <ListItemCanvas padding={customListItemPadding ?? listItemPadding} {...otherProps}>
            {baseContent}
        </ListItemCanvas>
    );
};
